import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isStepTestMetaRelPath } from "../../shared/stepTests/constants";
import { templateSteps } from "../../shared/templateSteps";
import { isEditableTarget } from "../lib/isEditableTarget";
import { rememberStepFile } from "../lib/lastOpenStepFile";
import type {
  StreamingBashWrite,
  StreamingFileEdit,
} from "../lib/parseStreamingBashWrite";
import { resolveWorkflowFileHref } from "../lib/resolveWorkflowFileHref";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import {
  CodeEditor,
  type CodeEditorHandle,
  type CodeSelection,
  type PinnedRange,
} from "./CodeEditor";
import { useCodeEditorSearch } from "./CodeEditor/useCodeEditorSearch";
import { Dropdown } from "./Dropdown";
import { ToggleButton, ToggleGroup } from "./HeadingToggle";
import { Icon } from "./Icon";
import { ReadmeContent } from "./ReadmeContent";
import { StepDetails } from "./StepDetails";
import {
  buildDiff,
  type LineOrigin,
  type MergedFile,
  mergeFiles,
  type StepFileData,
} from "./stepFilesDiff";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import type { CodeSnippet } from "./Workflow";

export type { StepFileData } from "./stepFilesDiff";

const HeadingOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 4px 8px;
  background: transparent;
  border-radius: 9999px;

  &:hover {
    background: var(--alpha4);
  }
`;

const FooterRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const FooterBadge = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px;
  margin-right: 2px;
  background: var(--alpha4);
  border-radius: 38px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ImagePreview = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 24px;
  overflow: auto;
  background: repeating-conic-gradient(var(--alpha4) 0% 25%, transparent 0% 50%)
    0 0 / 20px 20px;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 6px;
    border: 1px solid var(--border-alpha);
    box-shadow: 0 1px 4px var(--alpha16);
  }
`;

type Hunk = { fromDocLine: number; toDocLine: number };

function computeHunks(origins: readonly LineOrigin[]): Hunk[] {
  const out: Hunk[] = [];
  let start = -1;
  origins.forEach((o, i) => {
    if (o.kind !== "unchanged") {
      if (start === -1) start = i;
    } else if (start !== -1) {
      out.push({ fromDocLine: start + 1, toDocLine: i });
      start = -1;
    }
  });
  if (start !== -1) {
    out.push({ fromDocLine: start + 1, toDocLine: origins.length });
  }
  return out;
}

const entrypointFiles = new Set(
  Object.values(templateSteps)
    .map((t) => t.entryPoint)
    .filter(Boolean)
);

const filePriority = (path: string) => {
  if (path === "README.md") return 0;
  if (entrypointFiles.has(path)) return 1;
  if (path.startsWith("tests/")) return 3;
  return 2;
};

type Props = {
  files: readonly StepFileData[];
  previousFiles?: readonly StepFileData[];
  canShowDiff?: boolean;
  stepName: string;
  allStepNames: readonly string[];
  onSelectionChange?: (selections: CodeSelection[]) => void;
  codeSnippets?: readonly CodeSnippet[];
  streamingFileWrite?: StreamingBashWrite | null;
  streamingFileEdit?: StreamingFileEdit | null;
};

export function StepFiles({
  files,
  previousFiles,
  canShowDiff,
  stepName,
  allStepNames,
  onSelectionChange,
  codeSnippets,
  streamingFileWrite,
  streamingFileEdit,
}: Props) {
  const {
    spaceId,
    workflowId,
    step,
    file: urlFile,
  } = useCurrentUrl(routes.space.workflow);

  const [diffMode, setDiffMode] = useState<boolean>(
    () => localStorage.getItem("stepCodeDiffMode") === "true"
  );

  const search = useCodeEditorSearch();

  const mergedFiles = useMemo(
    () => mergeFiles(files, previousFiles),
    [files, previousFiles]
  );

  const hasChanges = useMemo(
    () => mergedFiles.some((f) => f.kind !== "unchanged"),
    [mergedFiles]
  );
  const baseDiffMode = canShowDiff && hasChanges && diffMode;
  const toggleDiffMode = () => {
    const next = !diffMode;
    setDiffMode(next);
    localStorage.setItem("stepCodeDiffMode", String(next));
  };

  const visibleFiles = useMemo(() => {
    const base = (
      baseDiffMode
        ? mergedFiles
        : mergedFiles.filter((f) => f.kind !== "removed")
    ).filter((f) => !isStepTestMetaRelPath(f.path));
    if (
      streamingFileWrite &&
      !base.some((f) => f.path === streamingFileWrite.filePath)
    ) {
      return [
        {
          path: streamingFileWrite.filePath,
          currentContent: streamingFileWrite.content,
          previousContent: null,
          kind: "added" as const,
        },
        ...base,
      ];
    }
    return base;
  }, [baseDiffMode, mergedFiles, streamingFileWrite]);

  const defaultFile =
    (streamingFileWrite &&
      visibleFiles.find((f) => f.path === streamingFileWrite.filePath)) ??
    (streamingFileEdit &&
      visibleFiles.find((f) => f.path === streamingFileEdit.filePath)) ??
    visibleFiles.find((file) => file.path === "README.md") ??
    visibleFiles.find((file) => entrypointFiles.has(file.path)) ??
    visibleFiles[0];

  const selectedPath =
    urlFile && visibleFiles.some((f) => f.path === urlFile)
      ? urlFile
      : defaultFile?.path;

  const setSelectedPath = useCallback(
    (path: string) => {
      rememberStepFile(workflowId, step, path);
      navigate(
        routes.space.workflow,
        { spaceId, workflowId, file: path },
        { replace: true }
      );
    },
    [spaceId, workflowId, step]
  );

  const selectedFile = visibleFiles.find((file) => file.path === selectedPath);

  const isStreamingThisFile =
    streamingFileWrite != null &&
    selectedFile?.path === streamingFileWrite.filePath;

  const streamingEdit = useMemo(() => {
    const content = selectedFile?.currentContent;
    if (
      streamingFileEdit == null ||
      streamingFileEdit.filePath !== selectedFile?.path ||
      !streamingFileEdit.newString ||
      content == null
    )
      return null;
    const idx = content.indexOf(streamingFileEdit.oldString);
    if (idx === -1) return null;
    const insertEnd = idx + streamingFileEdit.newString.length;
    const text =
      content.slice(0, idx) +
      streamingFileEdit.newString +
      content.slice(idx + streamingFileEdit.oldString.length);
    let line = 1;
    for (let i = 0; i < insertEnd; i++) {
      if (text.charCodeAt(i) === 10) line++;
    }
    return { text, line };
  }, [streamingFileEdit, selectedFile?.path, selectedFile?.currentContent]);

  const streamingEditStartLine = useMemo(() => {
    const content = selectedFile?.currentContent;
    if (
      streamingFileEdit == null ||
      streamingFileEdit.filePath !== selectedFile?.path ||
      !streamingFileEdit.oldString ||
      content == null
    )
      return null;
    const idx = content.indexOf(streamingFileEdit.oldString);
    if (idx === -1 || idx !== content.lastIndexOf(streamingFileEdit.oldString))
      return null;
    let line = 1;
    for (let i = 0; i < idx; i++) if (content.charCodeAt(i) === 10) line++;
    return line;
  }, [streamingFileEdit, selectedFile?.path, selectedFile?.currentContent]);

  const effectiveDiffMode =
    baseDiffMode && !isStreamingThisFile && !streamingEdit;

  const resolveStepFileHref = useCallback(
    (href: string) => resolveWorkflowFileHref(href, allStepNames, stepName),
    [stepName, allStepNames]
  );

  const diffBuild = useMemo(() => {
    if (!effectiveDiffMode || !selectedFile) return undefined;
    return buildDiff(
      selectedFile.previousContent ?? "",
      selectedFile.currentContent ?? ""
    );
  }, [effectiveDiffMode, selectedFile]);

  const editorValue = isStreamingThisFile
    ? streamingFileWrite.content
    : streamingEdit
      ? streamingEdit.text
      : effectiveDiffMode
        ? (diffBuild?.doc ?? "")
        : (selectedFile?.currentContent ?? "");

  // copyUploadToStep stores images as a data URI in the text commit store; render
  // them as the image rather than a wall of base64 (but show the diff as text).
  const imageDataUri =
    selectedFile &&
    !effectiveDiffMode &&
    !isStreamingThisFile &&
    !streamingEdit &&
    selectedFile.currentContent?.startsWith("data:image/")
      ? selectedFile.currentContent
      : null;

  const lineNumberMap = useMemo(
    () =>
      effectiveDiffMode && diffBuild
        ? diffBuild.lineOrigins.map((o) => o.sourceLine)
        : undefined,
    [effectiveDiffMode, diffBuild]
  );

  const pinnedRanges = useMemo<PinnedRange[] | undefined>(() => {
    if (!codeSnippets?.length || !selectedFile) return undefined;
    const expected = `${stepName}/${selectedFile.path}`;

    if (effectiveDiffMode && diffBuild) {
      const doc = diffBuild.doc;
      const origins = diffBuild.lineOrigins;
      const lineOffsets: number[] = [0];
      for (let i = 0; i < doc.length; i++) {
        if (doc[i] === "\n") lineOffsets.push(i + 1);
      }
      const matching = codeSnippets.filter((s) => s.filePath === expected);
      const ranges: PinnedRange[] = [];
      for (const s of matching) {
        const targetSide: "old" | "new" =
          s.revision === "previous" ? "old" : "new";
        let startDocLine = -1;
        let endDocLine = -1;
        for (let i = 0; i < origins.length; i++) {
          const origin = origins[i]!;
          if (
            origin.side === targetSide &&
            origin.sourceLine >= s.fromLine &&
            origin.sourceLine <= s.toLine
          ) {
            if (startDocLine === -1) startDocLine = i;
            endDocLine = i;
          }
        }
        if (startDocLine === -1) continue;
        const firstOrigin = origins[startDocLine]!;
        const lastOrigin = origins[endDocLine]!;
        const startLineStart = lineOffsets[startDocLine] ?? 0;
        const endLineStart = lineOffsets[endDocLine] ?? 0;
        const endLineEnd =
          lineOffsets[endDocLine + 1] != null
            ? lineOffsets[endDocLine + 1]! - 1
            : doc.length;
        const fromChar = firstOrigin.sourceLine === s.fromLine ? s.fromChar : 0;
        const toChar =
          lastOrigin.sourceLine === s.toLine
            ? s.toChar
            : endLineEnd - endLineStart;
        const from = startLineStart + fromChar;
        const to = Math.min(endLineStart + toChar, doc.length);
        ranges.push({ from, to });
      }
      return ranges.length ? ranges : undefined;
    }

    if (selectedFile.currentContent == null) return undefined;
    const matching = codeSnippets.filter(
      (s) => s.filePath === expected && s.revision !== "previous"
    );
    if (matching.length === 0) return undefined;
    const content = selectedFile.currentContent;
    const lines = content.split("\n");
    const lineOffsets: number[] = [0];
    for (let i = 0; i < lines.length; i++) {
      lineOffsets.push(lineOffsets[i]! + lines[i]!.length + 1);
    }
    return matching.map((s) => ({
      from: (lineOffsets[s.fromLine - 1] ?? 0) + s.fromChar,
      to: Math.min((lineOffsets[s.toLine - 1] ?? 0) + s.toChar, content.length),
    }));
  }, [codeSnippets, selectedFile, stepName, effectiveDiffMode, diffBuild]);

  const editorRef = useRef<CodeEditorHandle>(null);
  const pendingHunkIndexRef = useRef<number | null>(null);
  const [currentHunkIndex, setCurrentHunkIndex] = useState(0);

  const changedFiles = useMemo(
    () =>
      mergedFiles
        .filter((f) => f.kind !== "unchanged")
        .toSorted((a, b) => filePriority(a.path) - filePriority(b.path)),
    [mergedFiles]
  );

  const hunksByPath = useMemo(() => {
    const result = new Map<string, Hunk[]>();
    if (!effectiveDiffMode) return result;
    for (const f of changedFiles) {
      const build = buildDiff(f.previousContent ?? "", f.currentContent ?? "");
      result.set(f.path, computeHunks(build.lineOrigins));
    }
    return result;
  }, [effectiveDiffMode, changedFiles]);

  const currentHunks = useMemo(
    () => (selectedFile ? (hunksByPath.get(selectedFile.path) ?? []) : []),
    [selectedFile, hunksByPath]
  );

  const totalChangedFiles = changedFiles.length;
  const changedFileIndex = selectedFile
    ? changedFiles.findIndex((f) => f.path === selectedFile.path)
    : -1;

  const hunksByPathRef = useRef(hunksByPath);
  hunksByPathRef.current = hunksByPath;
  const changedFilesRef = useRef(changedFiles);
  changedFilesRef.current = changedFiles;

  // Auto-scroll to the first hunk (or pending one from cross-file nav) when
  // the file or diff mode changes, so the counter and viewport are in sync.
  useEffect(() => {
    if (!effectiveDiffMode) return;
    const target = pendingHunkIndexRef.current ?? 0;
    pendingHunkIndexRef.current = null;
    setCurrentHunkIndex(target);
    const file = changedFilesRef.current.find((f) => f.path === selectedPath);
    const hunks = file ? (hunksByPathRef.current.get(file.path) ?? []) : [];
    const hunk = hunks[target];
    if (hunk) editorRef.current?.scrollToLine(hunk.fromDocLine);
  }, [selectedPath, effectiveDiffMode]);

  const scrollToHunkInCurrentFile = useCallback(
    (index: number) => {
      const hunk = currentHunks[index];
      if (!hunk) return;
      editorRef.current?.scrollToLine(hunk.fromDocLine);
    },
    [currentHunks]
  );

  const streamingContent = isStreamingThisFile
    ? streamingFileWrite.content
    : null;
  useEffect(() => {
    if (streamingContent == null) return;
    let line = 1;
    for (let i = 0; i < streamingContent.length; i++) {
      if (streamingContent.charCodeAt(i) === 10) line++;
    }
    editorRef.current?.revealLine(line);
  }, [streamingContent]);

  useEffect(() => {
    if (streamingEditStartLine != null)
      editorRef.current?.revealLine(streamingEditStartLine);
  }, [streamingEditStartLine]);

  useEffect(() => {
    if (streamingEdit) editorRef.current?.revealLine(streamingEdit.line);
  }, [streamingEdit]);

  const goNextFile = useCallback(() => {
    if (totalChangedFiles === 0) return;
    const startIdx = changedFileIndex >= 0 ? changedFileIndex : -1;
    const nextIdx = (startIdx + 1) % totalChangedFiles;
    if (nextIdx === changedFileIndex) return;
    pendingHunkIndexRef.current = 0;
    setSelectedPath(changedFiles[nextIdx]!.path);
  }, [totalChangedFiles, changedFileIndex, changedFiles, setSelectedPath]);

  const goPrevFile = useCallback(() => {
    if (totalChangedFiles === 0) return;
    const startIdx = changedFileIndex >= 0 ? changedFileIndex : 0;
    const prevIdx = (startIdx - 1 + totalChangedFiles) % totalChangedFiles;
    if (prevIdx === changedFileIndex) return;
    pendingHunkIndexRef.current = 0;
    setSelectedPath(changedFiles[prevIdx]!.path);
  }, [totalChangedFiles, changedFileIndex, changedFiles, setSelectedPath]);

  const goNextDiff = useCallback(() => {
    if (currentHunks.length === 0) return;
    const newIndex = (currentHunkIndex + 1) % currentHunks.length;
    setCurrentHunkIndex(newIndex);
    scrollToHunkInCurrentFile(newIndex);
  }, [currentHunkIndex, currentHunks.length, scrollToHunkInCurrentFile]);

  const goPrevDiff = useCallback(() => {
    if (currentHunks.length === 0) return;
    const newIndex =
      (currentHunkIndex - 1 + currentHunks.length) % currentHunks.length;
    setCurrentHunkIndex(newIndex);
    scrollToHunkInCurrentFile(newIndex);
  }, [currentHunkIndex, currentHunks.length, scrollToHunkInCurrentFile]);

  const handleVisibleLineChange = useCallback(
    (line: number | null) => {
      if (line == null) return;
      if (currentHunks.length === 0) {
        setCurrentHunkIndex(0);
        return;
      }
      // 2-line tolerance prevents off-by-one ping-pong: after a smooth scroll
      // settles slightly above a hunk's first line, we'd otherwise drop back
      // to the previous hunk and the next click would just re-advance to the
      // same one.
      const TOLERANCE = 2;
      let foundIndex = 0;
      for (let i = 0; i < currentHunks.length; i++) {
        if (currentHunks[i]!.fromDocLine - TOLERANCE <= line) {
          foundIndex = i;
        } else {
          break;
        }
      }
      setCurrentHunkIndex(foundIndex);
    },
    [currentHunks]
  );

  useEffect(() => {
    if (!effectiveDiffMode || totalChangedFiles === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "n") {
        e.preventDefault();
        goNextDiff();
      } else if (e.key === "p") {
        e.preventDefault();
        goPrevDiff();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [effectiveDiffMode, totalChangedFiles, goNextDiff, goPrevDiff]);

  useEffect(() => {
    if (!canShowDiff || !hasChanges) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (e.key !== "d" && e.key !== "D") return;
      e.preventDefault();
      setDiffMode((v) => {
        const next = !v;
        localStorage.setItem("stepCodeDiffMode", String(next));
        return next;
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canShowDiff, hasChanges]);

  const handleSelectionChange = useCallback(
    (selections: CodeSelection[]) => {
      if (!onSelectionChange) return;
      if (!effectiveDiffMode || !diffBuild) {
        onSelectionChange(selections);
        return;
      }
      const origins = diffBuild.lineOrigins;
      const doc = diffBuild.doc;
      const lineStarts: number[] = [0];
      for (let i = 0; i < doc.length; i++) {
        if (doc[i] === "\n") lineStarts.push(i + 1);
      }
      const translated: CodeSelection[] = [];
      for (const sel of selections) {
        let runStart = sel.fromLine;
        let runSide: "old" | "new" | null = null;
        const flush = (end: number) => {
          if (runSide == null) return;
          const firstOrigin = origins[runStart - 1];
          const lastOrigin = origins[end - 1];
          if (!firstOrigin || !lastOrigin) return;
          const runStartLineStart = lineStarts[runStart - 1] ?? 0;
          const endLineStart = lineStarts[end - 1] ?? 0;
          const endLineEnd =
            lineStarts[end] != null ? lineStarts[end]! - 1 : doc.length;
          const fromCharOut = runStart === sel.fromLine ? sel.fromChar : 0;
          const toCharOut =
            end === sel.toLine ? sel.toChar : endLineEnd - endLineStart;
          const fromAbs = runStartLineStart + fromCharOut;
          const toAbs = endLineStart + toCharOut;
          const text = doc.slice(fromAbs, toAbs);
          translated.push({
            id: `${sel.filePath}:${runSide}:${firstOrigin.sourceLine}:${fromCharOut}-${lastOrigin.sourceLine}:${toCharOut}`,
            filePath: sel.filePath,
            from: fromAbs,
            to: toAbs,
            fromLine: firstOrigin.sourceLine,
            toLine: lastOrigin.sourceLine,
            fromChar: fromCharOut,
            toChar: toCharOut,
            text,
            revision: runSide === "old" ? "previous" : "current",
          });
        };
        for (let docLine = sel.fromLine; docLine <= sel.toLine; docLine++) {
          const origin = origins[docLine - 1];
          if (!origin) continue;
          if (runSide == null) {
            runSide = origin.side;
            runStart = docLine;
            continue;
          }
          if (origin.side !== runSide) {
            flush(docLine - 1);
            runSide = origin.side;
            runStart = docLine;
          }
        }
        flush(sel.toLine);
      }
      onSelectionChange(translated);
    },
    [diffBuild, effectiveDiffMode, onSelectionChange]
  );

  const fileButton = (file: MergedFile, label: string) => ({
    type: "button" as const,
    key: file.path,
    label: (
      <>
        {label}
        {file.kind !== "unchanged" && (
          <span
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              marginLeft: 6,
              borderRadius: "50%",
              verticalAlign: "middle",
              background:
                file.kind === "removed" ? "var(--red-600)" : "var(--green-600)",
            }}
          />
        )}
      </>
    ),
    left: <Icon name="code" size={16} />,
    right:
      file.path === selectedPath ? (
        <Icon name="check" size={12} color="var(--text-secondary)" />
      ) : undefined,
    onClick: () => setSelectedPath(file.path),
  });

  // Top-level files stay flat; files in subdirectories collapse into one
  // drill-in folder per directory so long paths show short names.
  const flatFileItems = visibleFiles
    .filter((file) => !file.path.includes("/"))
    .toSorted((a, b) => filePriority(a.path) - filePriority(b.path))
    .map((file) => fileButton(file, file.path));

  const filesByDir = new Map<string, MergedFile[]>();
  for (const file of visibleFiles) {
    if (!file.path.includes("/")) continue;
    const dir = file.path.slice(0, file.path.lastIndexOf("/"));
    const existing = filesByDir.get(dir);
    if (existing) existing.push(file);
    else filesByDir.set(dir, [file]);
  }
  const folderItems = [...filesByDir.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([dir, dirFiles]) => ({
      type: "submenu" as const,
      key: dir,
      label: dir,
      left: <Icon name="folder" size={16} />,
      selected: selectedPath?.startsWith(`${dir}/`) ?? false,
      submenu: [
        {
          items: dirFiles
            .toSorted((a, b) => a.path.localeCompare(b.path))
            .map((file) => fileButton(file, file.path.slice(dir.length + 1))),
        },
      ],
    }));

  const fileDropdownItems = [...flatFileItems, ...folderItems];

  const isReadmeView =
    !!selectedFile &&
    !imageDataUri &&
    selectedFile.path.endsWith(".md") &&
    !effectiveDiffMode;

  return (
    <search.Provider>
      <StepDetails
        flushBody={isReadmeView}
        heading={
          <Dropdown
            align="bottom-left"
            opener={
              <HeadingOpener>
                <span style={{ display: "flex" }}>
                  <Icon name="code" size={12} />
                </span>
                <Text variant="text-small-md">{selectedPath}</Text>
                <Icon name="chevron-down" size={12} />
              </HeadingOpener>
            }
            sections={[
              {
                label: "Files",
                items: fileDropdownItems,
              },
            ]}
          />
        }
        headingRight={
          selectedFile &&
          !imageDataUri &&
          (!selectedFile.path.endsWith(".md") || effectiveDiffMode) ? (
            <ToggleGroup>
              {canShowDiff && hasChanges && (
                <Tooltip
                  content={
                    <>
                      {effectiveDiffMode ? "Hide diffs" : "Show diffs"}{" "}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--tooltip-fg-muted)",
                          marginLeft: 4,
                        }}
                      >
                        ⌘⇧D
                      </span>
                    </>
                  }
                >
                  <ToggleButton
                    onPress={toggleDiffMode}
                    aria-pressed={!!effectiveDiffMode}
                  >
                    <Icon name="diffs" size={12} />
                  </ToggleButton>
                </Tooltip>
              )}
              {search.toggleButton}
            </ToggleGroup>
          ) : null
        }
        subheader={search.subheader}
        footer={
          effectiveDiffMode && totalChangedFiles > 0 ? (
            <>
              {totalChangedFiles >= 2 ? (
                <FooterRow>
                  <FooterBadge>
                    <Text variant="text-small-md" color="var(--text-primary)">
                      Files
                    </Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {changedFileIndex >= 0 ? changedFileIndex + 1 : 0} /{" "}
                      {totalChangedFiles}
                    </Text>
                  </FooterBadge>
                  <Tooltip content="Previous file">
                    <ToggleButton onPress={goPrevFile}>
                      <Icon name="arrow-up" size={12} />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip content="Next file">
                    <ToggleButton onPress={goNextFile}>
                      <Icon name="arrow-down" size={12} />
                    </ToggleButton>
                  </Tooltip>
                </FooterRow>
              ) : (
                <div />
              )}
              <FooterRow>
                <FooterBadge>
                  <Text variant="text-small-md" color="var(--text-primary)">
                    Diffs
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {currentHunks.length === 0 ? 0 : currentHunkIndex + 1} /{" "}
                    {currentHunks.length}
                  </Text>
                </FooterBadge>
                <Tooltip
                  content={
                    <>
                      Previous diff{" "}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--tooltip-fg-muted)",
                          marginLeft: 4,
                        }}
                      >
                        P
                      </span>
                    </>
                  }
                >
                  <ToggleButton
                    onPress={goPrevDiff}
                    disabled={currentHunks.length === 0}
                  >
                    <Icon name="arrow-up" size={12} />
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  content={
                    <>
                      Next diff{" "}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--tooltip-fg-muted)",
                          marginLeft: 4,
                        }}
                      >
                        N
                      </span>
                    </>
                  }
                >
                  <ToggleButton
                    onPress={goNextDiff}
                    disabled={currentHunks.length === 0}
                  >
                    <Icon name="arrow-down" size={12} />
                  </ToggleButton>
                </Tooltip>
              </FooterRow>
            </>
          ) : null
        }
        body={
          selectedFile ? (
            imageDataUri ? (
              <ImagePreview>
                <img src={imageDataUri} alt={selectedFile.path} />
              </ImagePreview>
            ) : isReadmeView ? (
              <ReadmeContent internalHref={resolveStepFileHref}>
                {editorValue}
              </ReadmeContent>
            ) : (
              <CodeEditor
                ref={editorRef}
                key={selectedFile.path}
                value={editorValue}
                filePath={selectedFile.path}
                onSelectionChange={
                  onSelectionChange ? handleSelectionChange : undefined
                }
                pinnedRanges={pinnedRanges}
                diffRanges={diffBuild?.diffRanges}
                lineNumberMap={lineNumberMap}
                onVisibleLineChange={
                  effectiveDiffMode ? handleVisibleLineChange : undefined
                }
              />
            )
          ) : null
        }
      />
    </search.Provider>
  );
}
